import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { lookupSession, requestLogout } from "@/config";
import { errors as errorCopy } from "@/content";
import { SESSION_EXPIRED_EVENT } from "@/constants/events";
import {
  clearReturnTo,
  currentCacheGeneration,
  getCachedSession,
  invalidateSessionCache,
  setCachedSession,
} from "@/hooks/shared/session";
import {
  SessionContext,
  setLastSignedInSession,
  type Session,
  type SessionState,
} from "@/context/session-context";

/**
 * Who is signed in, resolved once at startup.
 *
 * ## Why this is asked of the server rather than read locally
 *
 * There is nothing local to read. The session lives in an `HttpOnly` cookie that
 * JavaScript cannot see, so the only way to know whether it is valid is to ask —
 * which is exactly the property that makes it worth having. The answer also
 * re-reads the user row, so a deactivated account, a changed role or a revoked
 * session takes effect on the next page load rather than whenever a request
 * happens to fail.
 *
 * The prototype did the opposite: it rehydrated the user object, **including
 * `role`**, from `localStorage` and trusted it. Its `getMe` existed, was
 * exported, and was imported by no component at all. So a deactivated user kept a
 * working interface until some request happened to 401 — and a role change never
 * took effect at all.
 *
 * ## The three states are distinct on purpose
 *
 * `loading` is not `signedOut`. Collapsing them shows the sign-in form for a
 * moment on every hard reload, and an operator who is already signed in sees a
 * flash of "please sign in" — which reads as having been logged out.
 *
 * Kept in its own module so Vite Fast Refresh can patch it — a file that also
 * exports hooks cannot hot-reload components.
 */
function initialSessionState(): SessionState {
  const cached = getCachedSession();
  if (cached === undefined) return { status: "loading" };
  if (cached) {
    setLastSignedInSession(cached);
    return { status: "signedIn", session: cached };
  }
  return { status: "signedOut" };
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(initialSessionState);

  const refresh = useCallback(async () => {
    const generation = currentCacheGeneration();
    const previous = getCachedSession();
    const result = await lookupSession<Session>();
    /* Sign-out may have run while this request was open — do not resurrect it. */
    if (generation !== currentCacheGeneration()) return;

    if (result.kind === "unavailable") {
      /* A 502/network blip must not clear a still-valid cookie session. Keep
         whatever we already knew; only the first paint may fall through to the
         sign-in form so the operator is not stuck on a skeleton forever. */
      if (previous) {
        setCachedSession(previous);
        setLastSignedInSession(previous);
        setState({ status: "signedIn", session: previous });
      } else {
        setState({ status: "signedOut" });
      }
      toast.error(errorCopy.networkUnavailable);
      return;
    }

    if (result.kind === "none") {
      setCachedSession(null);
      setLastSignedInSession(null);
      setState({ status: "signedOut" });
      return;
    }

    setCachedSession(result.data);
    setLastSignedInSession(result.data);
    setState({ status: "signedIn", session: result.data });
  }, []);

  /* Session is normally resolved in `main.tsx` before the first paint. This effect
     only runs when something mounted without that bootstrap (tests, Storybook),
     or when the bootstrap probe got an unavailable API and left the cache empty. */
  useEffect(() => {
    if (getCachedSession() !== undefined) return;
    void refresh();
  }, [refresh]);

  /* A 401 from anywhere in the app lands here. One listener, so there is one
     place that decides what "signed out" means — rather than every call site
     deciding whether to redirect. */
  useEffect(() => {
    const onExpired = () => {
      invalidateSessionCache();
      setCachedSession(null);
      setLastSignedInSession(null);
      setState({ status: "signedOut" });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const signOut = useCallback(() => {
    /* Local first, network second (and never blocking the UI). Waiting on
       `/auth/logout` was why the button felt stuck: the API round-trip on a
       cold/local DB can take seconds, and an in-flight `resolveSession` could
       then repopulate the cache from the still-valid cookie and bounce
       `/login` straight back to the dashboard. */
    clearReturnTo();
    invalidateSessionCache();
    setCachedSession(null);
    setLastSignedInSession(null);
    setState({ status: "signedOut" });
    void requestLogout();
  }, []);

  const value = useMemo(
    () => ({ ...state, refresh, signOut }),
    [state, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
